import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  Suspense,
  lazy,
  // StrictMode, // ✅ Removido para evitar warning do findDOMNode com Material-UI
  useRef,
} from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Grid, Typography, Box } from "@material-ui/core";
import { useToast } from "../../hooks/useToast";
import { isArray, isEmpty } from "lodash";
import moment from "moment";

import DashboardIcon from "@material-ui/icons/Dashboard";
import BarChartIcon from "@material-ui/icons/BarChart";
import PieChartIcon from "@material-ui/icons/PieChart";
import TimelineIcon from "@material-ui/icons/Timeline";
import CategoryIcon from "@material-ui/icons/Category";
import ScheduleIcon from "@material-ui/icons/Schedule";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import useDashboard from "../../hooks/useDashboard";
import useMessages from "../../hooks/useMessages";
import { DashboardProvider } from "../../context/Dashboard/DashboardContext";
import api from "../../services/api";

import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import TopContactsTable from "../../components/Dashboard/TopContactsTable";
import Title from "../../components/Title";

import DashboardHeader from "../../components/Dashboard/DashboardHeader";
import MetricCards from "../../components/Dashboard/MetricCards";
import ModernFilters from "../../components/Dashboard/ModernFilters";
import LazyLoadingIndicator from "../../components/Dashboard/LazyLoadingIndicator";

const TicketsOverviewChart = lazy(() =>
  import("../../components/Dashboard/TicketsOverviewChart")
);
const NPSChart = lazy(() => import("../../components/Dashboard/NPSChart"));
const MessagesChart = lazy(() =>
  import("../../components/Dashboard/MessagesChart")
);
const TimeMetricsChart = lazy(() =>
  import("../../components/Dashboard/TimeMetricsChart")
);
const ChannelDistributionChart = lazy(() =>
  import("../../components/Dashboard/ChannelDistributionChart")
);
const QueueDistributionChart = lazy(() =>
  import("../../components/Dashboard/QueueDistributionChart")
);
const DailyActivityChart = lazy(() =>
  import("../../components/Dashboard/DailyActivityChart")
);
const HourlyActivityChart = lazy(() =>
  import("../../components/Dashboard/HourlyActivityChart")
);

const LazyLoadingFallback = () => (
  <LazyLoadingIndicator size="50px" minHeight="300px" />
);

export const dashboardChartColors = {
  primary: ["#4254cb", "#6a75ca", "#8e99e5", "#b8bfff"],
  secondary: ["#e53935", "#f56c67", "#ff9e99", "#ffcdd2"],
  success: ["#06c270", "#39d990", "#6bfab6", "#9cffd7"],
  warning: ["#ffc107", "#ffce38", "#ffde67", "#ffecb3"],
  neutral: ["#607d8b", "#8eacbb", "#b0bec5", "#cfd8dc"],
  info: ["#03a9f4", "#4fc3f7", "#81d4fa", "#b3e5fc"],
  charts: {
    donut: ["#4254cb", "#03a9f4", "#06c270", "#ffc107", "#e53935", "#607d8b"],
    bar: ["#4254cb", "#6a75ca", "#8e99e5", "#b8bfff"],
    line: ["#4254cb", "#e53935", "#06c270", "#ffc107"],
    area: ["#4254cb20", "#e5393520", "#06c27020", "#ffc10720"],
    mix: ["#4254cb", "#e53935", "#06c270", "#ffc107", "#03a9f4", "#607d8b"],
  },
};

const useStyles = makeStyles((theme) => ({
  dashboardContainer: {
    position: "relative",
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(1),
    transition: "all 0.3s ease-in-out",
  },
  fullscreenContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: theme.palette.background.default,
    zIndex: 9999,
    padding: theme.spacing(1),
    overflow: "auto",
    ...theme.scrollbarStyles,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  section: {
    marginBottom: theme.spacing(3),
    border:
      theme.palette.type === "dark"
        ? `1px solid ${theme.palette.divider}`
        : `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.borderRadius?.medium || 12,
    padding: theme.spacing(2),
    boxShadow:
      theme.palette.type === "dark"
        ? "0 4px 12px rgba(0, 0, 0, 0.35)"
        : "0 4px 12px rgba(0, 0, 0, 0.05)",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      boxShadow:
        theme.palette.type === "dark"
          ? "0 6px 16px rgba(0, 0, 0, 0.4)"
          : "0 6px 16px rgba(0, 0, 0, 0.1)",
    },
    backgroundColor: theme.palette.background.paper,
  },
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
  },
  titleContainer: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8),
    height: "50vh",
  },
  loadingSpinner: {
    position: "relative",
    width: "80px",
    height: "80px",
    "&:before": {
      content: '""',
      boxSizing: "border-box",
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      border: "4px solid transparent",
      borderTopColor: theme.palette.primary.main,
      borderBottomColor: theme.palette.primary.main,
      animation: "$spinner 1.2s cubic-bezier(0.68, -0.55, 0.27, 1.55) infinite",
    },
    "&:after": {
      content: '""',
      boxSizing: "border-box",
      position: "absolute",
      top: "10px",
      left: "10px",
      width: "calc(100% - 20px)",
      height: "calc(100% - 20px)",
      borderRadius: "50%",
      border: "3px solid transparent",
      borderLeftColor: theme.palette.primary.main,
      borderRightColor: theme.palette.primary.main,
      animation:
        "$spinner 0.8s cubic-bezier(0.68, -0.55, 0.27, 1.55) infinite reverse",
    },
  },
  "@keyframes spinner": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
  loadingText: {
    marginTop: theme.spacing(4),
    opacity: 0.8,
    textAlign: "center",
  },
  loadingOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    zIndex: 9999,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backdropFilter: "blur(4px)",
    transition: "opacity 0.3s ease",
  },
  loadingCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    padding: theme.spacing(3),
    boxShadow: theme.shadows[10],
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "90%",
    width: 300,
  },
}));

const MessageCounter = React.memo(({ fromMe, dateStart, dateEnd, all }) => {
  let props = {};
  if (all) {
    props = { fromMe };
  } else {
    props = { fromMe, dateStart, dateEnd };
  }

  const { count } = useMessages(props);
  return count;
});

const Dashboard = () => {
  const classes = useStyles();

  const toast = useToast();
  const theme = useTheme();

  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [extendedMetrics, setExtendedMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messagesReceived, setMessagesReceived] = useState(0);
  const [messagesSent, setMessagesSent] = useState(0);
  const [shouldLoadData, setShouldLoadData] = useState(false);
  const {
    findWithExtendedMetrics,
    getExtendedMetrics,
    preloadDashboardData,
    forceRefresh,
  } = useDashboard();

  const [visibleSections, setVisibleSections] = useState(() => {
    try {
      const savedSections = localStorage.getItem("dashboardVisibleSections");
      return savedSections
        ? JSON.parse(savedSections)
        : {
            metrics: true,
            overview: true,
            messages: true,
            attendants: true,
            distribution: true,
            hourly: true,
          };
    } catch (error) {
      console.error("Erro ao carregar seções do localStorage:", error);
      return {
        metrics: true,
        overview: true,
        messages: true,
        attendants: true,
        distribution: true,
        hourly: true,
      };
    }
  });

  const [refreshInterval, setRefreshInterval] = useState(() => {
    try {
      return localStorage.getItem("dashboardRefreshInterval") || "manual";
    } catch (error) {
      console.error("Erro ao carregar intervalo do localStorage:", error);
      return "manual";
    }
  });

  const refreshTimerRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(
        "dashboardVisibleSections",
        JSON.stringify(visibleSections)
      );
    } catch (error) {
      console.error("Erro ao salvar seções no localStorage:", error);
    }
  }, [visibleSections]);

  useEffect(() => {
    try {
      localStorage.setItem("dashboardRefreshInterval", refreshInterval);
    } catch (error) {
      console.error("Erro ao salvar intervalo no localStorage:", error);
    }
  }, [refreshInterval]);

  const today = useMemo(() => new Date(), []);

  const [dateStartTicket, setDateStartTicket] = useState(() =>
    moment(today).format("YYYY-MM-DD")
  );

  const [dateEndTicket, setDateEndTicket] = useState(
    moment(today).format("YYYY-MM-DD")
  );

  const [queueTicket, setQueueTicket] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [filterWhatsappId, setFilterWhatsappId] = useState("");
  const [filterChannel, setFilterChannel] = useState("");
  const [filterTicketStatus, setFilterTicketStatus] = useState("");
  const [filterTagIds, setFilterTagIds] = useState([]);
  const [fetchDataFilter, setFetchDataFilter] = useState(false);

  const { user, socket } = useContext(AuthContext);

  const formatTime = useCallback((minutes) => {
    return moment()
      .startOf("day")
      .add(minutes, "minutes")
      .format("HH[h] mm[m]");
  }, []);

  const resetExtraFilters = useCallback(() => {
    setQueueTicket("");
    setFilterUserId("");
    setFilterWhatsappId("");
    setFilterChannel("");
    setFilterTicketStatus("");
    setFilterTagIds([]);
  }, []);

  const apiParams = useMemo(() => {
    let params = {};

    if (!isEmpty(dateStartTicket) && moment(dateStartTicket).isValid()) {
      params = {
        ...params,
        date_from: moment(dateStartTicket).format("YYYY-MM-DD"),
      };
    }

    if (!isEmpty(dateEndTicket) && moment(dateEndTicket).isValid()) {
      params = {
        ...params,
        date_to: moment(dateEndTicket).format("YYYY-MM-DD"),
      };
    }

    if (queueTicket) {
      const qn = parseInt(String(queueTicket), 10);
      if (!isNaN(qn)) {
        params = {
          ...params,
          queueIds: JSON.stringify([qn]),
        };
      }
    }
    if (filterUserId) {
      const u = parseInt(String(filterUserId), 10);
      if (!isNaN(u)) {
        params = { ...params, userId: String(u) };
      }
    }
    if (filterWhatsappId) {
      const w = parseInt(String(filterWhatsappId), 10);
      if (!isNaN(w)) {
        params = { ...params, whatsappId: String(w) };
      }
    }
    if (filterChannel) {
      params = { ...params, channel: filterChannel };
    }
    if (filterTicketStatus) {
      params = { ...params, ticketStatus: filterTicketStatus };
    }
    if (Array.isArray(filterTagIds) && filterTagIds.length > 0) {
      const tagNums = filterTagIds
        .map((x) => parseInt(String(x), 10))
        .filter((n) => !isNaN(n));
      if (tagNums.length) {
        params = { ...params, tagIds: JSON.stringify(tagNums) };
      }
    }

    if (Object.keys(params).length === 0) {
      params = {
        date_from: moment(today).format("YYYY-MM-DD"),
        date_to: moment(today).format("YYYY-MM-DD"),
      };
    }

    return params;
  }, [
    dateStartTicket,
    dateEndTicket,
    queueTicket,
    filterUserId,
    filterWhatsappId,
    filterChannel,
    filterTicketStatus,
    filterTagIds,
    today,
  ]);

  const fetchData = useCallback(
    async (forceUpdate = false) => {
      setLoading(true);

      try {
        let data;

        if (forceUpdate) {
          data = await findWithExtendedMetrics(apiParams, { skipCache: true });
        } else {
          data = await findWithExtendedMetrics(apiParams);
        }

        setCounters(data.counters || {});
        if (isArray(data.attendants)) {
          // ✅ Normalizar attendants para garantir que name seja sempre string
          const normalizedAttendants = data.attendants.map(att => ({
            ...att,
            name: (att?.name && typeof att.name === 'string') ? att.name : (att?.name ? String(att.name) : 'Sem nome')
          }));
          setAttendants(normalizedAttendants);
        } else {
          setAttendants([]);
        }

        if (data.extendedMetrics) {
          setExtendedMetrics(data.extendedMetrics);
        } else {
          try {
            const metricsData = await getExtendedMetrics(apiParams);
            setExtendedMetrics(metricsData || null);
          } catch (err) {
            // Silenciosamente ignorar erro de métricas estendidas (rota pode não existir)
            console.log("Métricas estendidas não disponíveis");
            setExtendedMetrics(null);
          }
        }

        // Buscar contagem de mensagens
        try {
          const [receivedRes, sentRes] = await Promise.all([
            api.get("/messages-allMe", { params: { fromMe: false, dateStart: dateStartTicket, dateEnd: dateEndTicket } }),
            api.get("/messages-allMe", { params: { fromMe: true, dateStart: dateStartTicket, dateEnd: dateEndTicket } })
          ]);
          setMessagesReceived(receivedRes.data.count || 0);
          setMessagesSent(sentRes.data.count || 0);
        } catch (err) {
          console.log("Erro ao buscar contagem de mensagens:", err);
          setMessagesReceived(0);
          setMessagesSent(0);
        }

        return data;
      } catch (error) {
        console.error("Erro ao buscar dados do dashboard:", error);
        // Não mostrar toast de erro, apenas retornar dados padrão
        return { counters: {}, attendants: [] };
      } finally {
        setLoading(false);
      }
    },
    [findWithExtendedMetrics, getExtendedMetrics, apiParams, dateStartTicket, dateEndTicket]
  );

  // ✅ CORRIGIDO: Usar useRef para fetchData e evitar loops
  const fetchDataRef = useRef(fetchData);
  
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  const handleForceRefresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await forceRefresh(apiParams);

      setCounters(data.counters);
      if (isArray(data.attendants)) {
        setAttendants(data.attendants);
      } else {
        setAttendants([]);
      }

      if (data.extendedMetrics) {
        setExtendedMetrics(data.extendedMetrics);
      }

      return data;
    } catch (error) {
      toast.error(
        i18n.t("An error occurred while refreshing data") ||
          "Erro ao atualizar dados"
      );
      throw error;
    } finally {
      setLoading(false);
    }
  }, [forceRefresh, apiParams, toast]);

  // ✅ CORRIGIDO: Usar useRef para armazenar handleForceRefresh e evitar recriação de intervals/listeners
  const handleForceRefreshRef = useRef(handleForceRefresh);
  
  // Atualizar ref quando handleForceRefresh mudar
  useEffect(() => {
    handleForceRefreshRef.current = handleForceRefresh;
  }, [handleForceRefresh]);

  useEffect(() => {
    // Limpar interval anterior
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (refreshInterval && refreshInterval !== "manual") {
      const minutes = parseInt(refreshInterval, 10);
      if (!isNaN(minutes) && minutes > 0) {
        console.log(
          `Configurando atualização automática a cada ${minutes} minutos`
        );

        // ✅ CORRIGIDO: Usar ref.current para evitar dependência de handleForceRefresh
        refreshTimerRef.current = setInterval(() => {
          console.log("Atualizando dados automaticamente...");
          handleForceRefreshRef.current().catch((err) => {
            console.error("Erro na atualização automática:", err);
          });
        }, minutes * 60 * 1000);
      }
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [refreshInterval]); // ✅ CORRIGIDO: Removido handleForceRefresh das dependências

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoadData(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (shouldLoadData) {
      // ✅ CORRIGIDO: Usar ref para evitar loops quando fetchData mudar
      fetchDataRef.current();
    }
  }, [fetchDataFilter, shouldLoadData]); // ✅ fetchDataFilter é apenas um trigger, não precisa de fetchData nas deps

  // ✅ CORRIGIDO: Usar ref para evitar recriação do listener quando handleForceRefresh mudar
  useEffect(() => {
    if (!socket) return;

    const onViewsRefreshed = () => {
      handleForceRefreshRef.current().catch((err) => {
        console.error("Erro ao atualizar após evento de socket:", err);
      });
    };

    socket.on("dashboard:viewsRefreshed", onViewsRefreshed);

    return () => {
      socket.off("dashboard:viewsRefreshed", onViewsRefreshed);
    };
  }, [socket]); // ✅ CORRIGIDO: Removido handleForceRefresh das dependências

  const GetUsers = useCallback(() => {
    let userOnline = 0;
    attendants.forEach((user) => {
      if (user.online === true) {
        userOnline = userOnline + 1;
      }
    });
    return userOnline === 0 ? 0 : userOnline;
  }, [attendants]);

  const GetMessages = useCallback(
    (all, fromMe) => {
      return (
        <MessageCounter
          fromMe={fromMe}
          dateStart={dateStartTicket}
          dateEnd={dateEndTicket}
          all={all}
        />
      );
    },
    [dateStartTicket, dateEndTicket]
  );

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prevState) => {
      const newState = !prevState;

      const dashboardElement = document.getElementById("dashboard-root");
      if (dashboardElement) {
        if (newState) {
          document.body.style.overflow = "";
          dashboardElement.style.position = "fixed";
          dashboardElement.style.top = "0";
          dashboardElement.style.left = "0";
          dashboardElement.style.width = "100vw";
          dashboardElement.style.height = "100vh";
          dashboardElement.style.zIndex = "9999";
          dashboardElement.style.padding = theme.spacing(1) + "px";
          dashboardElement.style.overflow = "auto";

          const navbar = document.querySelector(".navbar");
          if (navbar) {
            navbar.style.visibility = "hidden";
            navbar.style.zIndex = "0";
          }
        } else {
          document.body.style.overflow = "";
          dashboardElement.style.position = "relative";
          dashboardElement.style.top = "auto";
          dashboardElement.style.left = "auto";
          dashboardElement.style.width = "auto";
          dashboardElement.style.height = "auto";
          dashboardElement.style.zIndex = "auto";
          dashboardElement.style.padding = theme.spacing(1) + "px";
          dashboardElement.style.overflow = "";

          const navbar = document.querySelector(".navbar");
          if (navbar) {
            navbar.style.visibility = "visible";
            navbar.style.zIndex = "1100";
          }
        }
      }

      return newState;
    });
  }, [theme]);

  useEffect(() => {
    if (isFullscreen) {
      const handleEscKey = (event) => {
        if (event.key === "Escape") {
          setIsFullscreen(false);
        }
      };

      window.addEventListener("keydown", handleEscKey);
      return () => {
        window.removeEventListener("keydown", handleEscKey);
      };
    }
  }, [isFullscreen]);

  const useIntersectionObserver = (nodeId, threshold = 0.1) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting);
        },
        { threshold }
      );

      const node = document.getElementById(nodeId);
      if (node) {
        observer.observe(node);
      }

      return () => {
        if (node) {
          observer.unobserve(node);
        }
      };
    }, [nodeId, threshold]);

    return isVisible;
  };

  const LazySection = React.memo(({ id, children, forceRender = false }) => {
    const isVisible = useIntersectionObserver(id);
    const [wasVisible, setWasVisible] = useState(false);
    const [content, setContent] = useState(null);

    useEffect(() => {
      if ((isVisible || forceRender) && !wasVisible) {
        setWasVisible(true);
        setContent(children);
      }
    }, [isVisible, wasVisible, forceRender, children]);

    const renderedContent = useMemo(() => {
      if (content) return content;

      if (forceRender || isVisible || wasVisible) {
        return children;
      }

      return <LazyLoadingIndicator size="40px" minHeight="300px" />;
    }, [isVisible, wasVisible, forceRender, children, content]);

    return (
      <section id={id} className={classes.section}>
        {renderedContent}
      </section>
    );
  });

  const dashboardContent = useMemo(
    () => (
      <>
        {!isFullscreen && (
          <div className={classes.titleContainer}>
            <Title>{i18n.t("dashboard.title") || "Dashboard"}</Title>
          </div>
        )}

        <DashboardHeader
          isFullscreen={isFullscreen}
          toggleFullscreen={toggleFullscreen}
          handleRefresh={handleForceRefresh}
          dateStartTicket={dateStartTicket}
          dateEndTicket={dateEndTicket}
          loading={loading}
        />

        <ModernFilters
          setDateStartTicket={setDateStartTicket}
          setDateEndTicket={setDateEndTicket}
          dateStartTicket={dateStartTicket}
          dateEndTicket={dateEndTicket}
          setQueueTicket={setQueueTicket}
          queueTicket={queueTicket}
          filterUserId={filterUserId}
          setFilterUserId={setFilterUserId}
          filterWhatsappId={filterWhatsappId}
          setFilterWhatsappId={setFilterWhatsappId}
          filterChannel={filterChannel}
          setFilterChannel={setFilterChannel}
          filterTicketStatus={filterTicketStatus}
          setFilterTicketStatus={setFilterTicketStatus}
          filterTagIds={filterTagIds}
          setFilterTagIds={setFilterTagIds}
          onResetExtraFilters={resetExtraFilters}
          fetchData={setFetchDataFilter}
          visibleSections={visibleSections}
          setVisibleSections={setVisibleSections}
          refreshInterval={refreshInterval}
          setRefreshInterval={setRefreshInterval}
        />

        {!shouldLoadData && (
          <LazyLoadingIndicator
            message={
              i18n.t("dashboard.loadingMessage") ||
              "Os dados do Dashboard serão carregados em breve..."
            }
            size="80px"
            fullHeight={false}
            minHeight="50vh"
          />
        )}

        {shouldLoadData && loading && (
          <div className={classes.loadingOverlay}>
            <div className={classes.loadingCard}>
              <div className={classes.loadingSpinner}></div>
              <Typography
                variant="h6"
                style={{ marginTop: 20, marginBottom: 5 }}
              >
                {i18n.t("dashboard.refreshing") || "Atualizando dados..."}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("dashboard.pleaseWait") || "Por favor, aguarde"}
              </Typography>
            </div>
          </div>
        )}

        {shouldLoadData && (
          <>
            {visibleSections.metrics && (
              <section id="metrics-section" className={classes.section}>
                <Typography component="div" className={classes.sectionTitle}>
                  <DashboardIcon style={{ marginRight: 8 }} fontSize="small" />
                  {i18n.t("dashboard.general.title") || "Informações Gerais"}
                </Typography>
                <MetricCards
                  counters={counters}
                  extendedMetrics={extendedMetrics}
                  formatTime={formatTime}
                  GetUsers={GetUsers}
                  GetMessages={GetMessages}
                  loading={loading}
                  attendantsList={attendants}
                  messagesReceived={messagesReceived}
                  messagesSent={messagesSent}
                />
              </section>
            )}

            {visibleSections.overview && (
              <LazySection id="overview-section" forceRender={true}>
                <Typography component="div" className={classes.sectionTitle}>
                  <PieChartIcon style={{ marginRight: 8 }} fontSize="small" />
                  {i18n.t("dashboard.overview.title") ||
                    "Visão Geral e Avaliações"}
                </Typography>
                <Grid container spacing={2} style={{ minHeight: "400px" }}>
                  <Grid item xs={12} md={4}>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <TicketsOverviewChart
                        counters={counters}
                        extendedMetrics={extendedMetrics}
                        title={
                          i18n.t("dashboard.tickets.overview") ||
                          "Visão Geral dos Tickets"
                        }
                        loading={loading}
                      />
                    </Suspense>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <TimeMetricsChart
                        counters={counters}
                        extendedMetrics={extendedMetrics}
                        title={
                          i18n.t("dashboard.timeMetrics") || "Métricas de Tempo"
                        }
                        loading={loading}
                      />
                    </Suspense>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <NPSChart
                        npsScore={counters?.npsScore || 0}
                        npsPromotersPerc={counters?.npsPromotersPerc || 0}
                        npsPassivePerc={counters?.npsPassivePerc || 0}
                        npsDetractorsPerc={counters?.npsDetractorsPerc || 0}
                        percRating={counters?.percRating || 0}
                        withRating={counters?.withRating || 0}
                        title={
                          i18n.t("dashboard.assessments.evaluationIndex") ||
                          "Índice de Avaliação (NPS)"
                        }
                        loading={loading}
                      />
                    </Suspense>
                  </Grid>
                </Grid>
              </LazySection>
            )}

            {visibleSections.messages && (
              <LazySection id="messages-section" forceRender={true}>
                <Typography component="div" className={classes.sectionTitle}>
                  <TimelineIcon style={{ marginRight: 8 }} fontSize="small" />
                  {i18n.t("dashboard.messages.title") ||
                    "Mensagens por Período"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <MessagesChart
                        dateStart={dateStartTicket}
                        dateEnd={dateEndTicket}
                        queueId={queueTicket}
                        title=""
                        loading={loading}
                      />
                    </Suspense>
                  </Grid>
                </Grid>
              </LazySection>
            )}

            {visibleSections.attendants && (
              <LazySection id="activity-section" forceRender={true}>
                <Typography component="div" className={classes.sectionTitle}>
                  <BarChartIcon style={{ marginRight: 8 }} fontSize="small" />
                  {i18n.t("dashboard.activity.title") ||
                    "Atividade por Período e Desempenho"}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box style={{ height: "700px" }}>
                      <Grid container spacing={2} style={{ height: "100%" }}>
                        <Grid
                          item
                          xs={12}
                          style={{ height: "350px", maxHeight: "350px" }}
                        >
                          <Suspense fallback={<LazyLoadingFallback />}>
                            <DailyActivityChart
                              data={extendedMetrics?.ticketsByDay}
                              loading={loading}
                              dateStart={apiParams.date_from}
                              dateEnd={apiParams.date_to}
                            />
                          </Suspense>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          style={{ height: "350px", maxHeight: "350px" }}
                        >
                          <Suspense fallback={<LazyLoadingFallback />}>
                            <TopContactsTable queryParams={apiParams} />
                          </Suspense>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} style={{ height: "700px" }}>
                    <TableAttendantsStatus
                      attendants={attendants}
                      loading={loading}
                      title={
                        i18n.t("dashboard.attendants.performance") ||
                        "Desempenho dos Atendentes"
                      }
                    />
                  </Grid>
                </Grid>
              </LazySection>
            )}

            {visibleSections.distribution && (
              <LazySection id="distribution-section" forceRender={true}>
                <Typography component="div" className={classes.sectionTitle}>
                  <CategoryIcon style={{ marginRight: 8 }} fontSize="small" />
                  {i18n.t("dashboard.distribution.title") ||
                    "Distribuição por Canal e Setor"}
                </Typography>
                <Grid container spacing={2} style={{ minHeight: "400px" }}>
                  <Grid item xs={12} md={6}>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <ChannelDistributionChart
                        data={extendedMetrics?.ticketsByChannel}
                        loading={loading}
                      />
                    </Suspense>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <QueueDistributionChart
                        data={extendedMetrics?.ticketsByQueue}
                        loading={loading}
                      />
                    </Suspense>
                  </Grid>
                </Grid>
              </LazySection>
            )}

            {visibleSections.hourly && (
              <LazySection id="hourly-section" forceRender={true}>
                <Typography component="div" className={classes.sectionTitle}>
                  <ScheduleIcon style={{ marginRight: 8 }} fontSize="small" />
                  {i18n.t("dashboard.hourly.title") || "Atividade por Hora"}
                </Typography>
                <Grid container spacing={2} style={{ minHeight: "400px" }}>
                  <Grid item xs={12}>
                    <Suspense fallback={<LazyLoadingFallback />}>
                      <HourlyActivityChart
                        data={extendedMetrics?.ticketsByHour}
                        loading={loading}
                      />
                    </Suspense>
                  </Grid>
                </Grid>
              </LazySection>
            )}
          </>
        )}
      </>
    ),
    [
      isFullscreen,
      shouldLoadData,
      visibleSections,
      counters,
      extendedMetrics,
      formatTime,
      GetUsers,
      GetMessages,
      loading,
      attendants,
      toggleFullscreen,
      handleForceRefresh,
      dateStartTicket,
      dateEndTicket,
      refreshInterval,
      setRefreshInterval,
      setDateStartTicket,
      setDateEndTicket,
      setQueueTicket,
      messagesReceived,
      messagesSent,
      queueTicket,
      setFetchDataFilter,
      setVisibleSections,
      apiParams.date_from,
      apiParams.date_to,
      classes.loadingCard,
      classes.loadingOverlay,
      classes.loadingSpinner,
      classes.section,
      classes.sectionTitle,
      classes.titleContainer,
    ]
  );

  // ✅ CORRIGIDO: Usar ref para preloadDashboardData e evitar loops
  const preloadDashboardDataRef = useRef(preloadDashboardData);
  
  useEffect(() => {
    preloadDashboardDataRef.current = preloadDashboardData;
  }, [preloadDashboardData]);

  useEffect(() => {
    const preloadCommonFilters = async () => {
      if (loading) return;

      const today = moment().format("YYYY-MM-DD");
      preloadDashboardDataRef.current({
        date_from: today,
        date_to: today,
      });

      const last7days = moment().subtract(6, "days").format("YYYY-MM-DD");
      preloadDashboardDataRef.current({
        date_from: last7days,
        date_to: today,
      });
    };

    if (shouldLoadData && !loading) {
      const timer = setTimeout(() => {
        preloadCommonFilters();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [loading, shouldLoadData]); // ✅ CORRIGIDO: Removido preloadDashboardData das dependências

  // ✅ CORRIGIDO: Removido StrictMode temporariamente para evitar warning do findDOMNode
  // O StrictMode causa warnings com componentes do Material-UI que usam findDOMNode
  return (
    <>
      {user.profile === "user" && user.showDashboard === "disabled" ? (
        <ForbiddenPage />
      ) : (
        <MainContainer>
          <div id="dashboard-root" className={classes.dashboardContainer}>
            {dashboardContent}
          </div>
        </MainContainer>
      )}
    </>
  );
};

const DashboardWithProvider = () => {
  return (
    <DashboardProvider>
      <Dashboard />
    </DashboardProvider>
  );
};

export default React.memo(DashboardWithProvider);
